import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import SOVAnalysisResults from 'modules/insights/sov/components/analysis/results/SOVAnalysisResults';
import SOVQueries from 'modules/insights/sov/components/queries/SOVQueries';
import SOVFilters from 'modules/insights/sov/components/filters/SOVFilters';
import SOVCalloutCollabary from 'modules/insights/sov/components/callout-v2/collabary/SOVCalloutCollabary';
import SOVCalloutFreemium from 'modules/insights/sov/components/callouts/freemium/SOVCalloutFreemium';

import { danger } from 'redux-store/reducers/common-reducer';
import { trackSOVEvent } from 'modules/insights/sov/tracking/sov-tracking';
import { fetchReport, resetReport, updateReport } from 'modules/insights/sov/reducers/reports/collabary/sov-reports';
import { fetchCountries } from 'modules/insights/sov/reducers/countries/collabary/sov-countries';
import { downloadAnalysis, resetAnalysis } from 'modules/insights/sov/reducers/analysis/collabary/sov-analysis';
import {
  createQuery as createQuerySingle,
  deleteQuery as deleteQuerySingle,
  duplicateQuery as duplicateQuerySingle,
  fetchQueries as fetchQueriesSingle,
  resetQueries as resetQueriesSingle,
  updateQuery as updateQuerySingle
} from 'modules/insights/sov/reducers/queries/collabary/queries/sov-queries-single';
import {
  createQuery as createQueryMulti,
  deleteQuery as deleteQueryMulti,
  duplicateQuery as duplicateQueryMulti,
  fetchQueries as fetchQueriesMulti,
  resetQueries as resetQueriesMulti,
  updateQuery as updateQueryMulti
} from 'modules/insights/sov/reducers/queries/collabary/queries/sov-queries-multi';

import { __t, applyTranslations } from 'utils/language';
import { ANALYSIS, COLLABARY, QUERY, REPORT, isReadyForAnalysis } from 'modules/insights/sov/utils/sov';
import {
  MULTI,
  SINGLE,
  getEndpointData,
  prepareQueryResultsMultiForEndpoint,
  prepareQueryResultsSingleForEndpoint
} from 'modules/insights/sov/utils/queries/sov-queries';

import type {
  LineGraphEChartsObject,
  LocationWindow,
  MixedSelectDropdownItem,
  Organization
} from 'types';
import type {
  SOVGeneralEventType,
  SOVGeneralProps,
  SOVGeneralVoidFunction,
  SOVQueryChannelsLocal,
  SOVQueryCommon,
  SOVQueryEndpoint,
  SOVQueryEndpointMulti,
  SOVQueryEndpointMultiGroup,
  SOVQueryLoading,
  SOVQueryLocal,
  SOVQuerySave,
  SOVQuerySearchResultsLocal,
  SOVQueryType,
  SOVReport,
  SOVReportType,
  SOVReportUpdateCallback
} from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));

type OwnProps = {
  organization: Organization,
  location: LocationWindow,
  brandProfileId: number | undefined | null,
  report: SOVReport,
  reportId: number,
  reportType: SOVReportType,
  graphInstance: LineGraphEChartsObject | undefined | null,
  isReportSaved: boolean,
  isAnalysisDownloading: boolean,
  sovTimeframeCanSelectPastTwoWeeks: boolean,
  onRenderBreadcrumbs: () => React.ReactNode,
  onRenderPageHeader: () => React.ReactNode,
  onRenderReportHeader: (b: SOVReportUpdateCallback, a?: SOVGeneralVoidFunction | null) => React.ReactNode,
  onReportSavedText: () => void,
  onChartInstanceCallback: (a: LineGraphEChartsObject) => void
};

type StateProps = {
  queriesSingle: Array<SOVQueryEndpoint>,
  queriesMulti: Array<SOVQueryEndpointMulti>,
  queriesMerged: Array<SOVQueryLocal>,
  results: SOVQuerySearchResultsLocal,
  resultsCustom: SOVQuerySearchResultsLocal,
  channels: SOVQueryChannelsLocal,
  countries: Array<MixedSelectDropdownItem>,
  hasAnalysisBeenRun: boolean,
  isLoadingQueries: SOVQueryLoading
};

type DispatchProps = {
  dispatchFetchReport: (brandProfileId: number, reportId: number) => Promise<void>,
  dispatchUpdateReport: (report: SOVReport) => Promise<void>,
  dispatchFetchCountries: () => Promise<void>,
  dispatchFetchQueriesSingle: (reportId: number) => Promise<void>,
  dispatchFetchQueriesMulti: (reportId: number) => Promise<void>,
  dispatchCreateQuerySingle: (query: SOVQueryEndpoint) => Promise<any>,
  dispatchCreateQueryMulti: (query: SOVQueryEndpoint) => Promise<any>,
  dispatchUpdateQuerySingle: (query: SOVQueryEndpoint) => Promise<any>,
  dispatchUpdateQueryMulti: (query: SOVQueryEndpoint) => Promise<any>,
  dispatchDuplicateQuerySingle: (reportId: number, queryId: number) => Promise<any>,
  dispatchDuplicateQueryMulti: (reportId: number, queryId: number) => Promise<any>,
  dispatchDeleteQuerySingle: (reportId: number, queryId: number) => Promise<void>,
  dispatchDeleteQueryMulti: (reportId: number, queryId: number) => Promise<void>,
  dispatchDownloadAnalysis: (report: SOVReport, chartImage: string, format: string) => void,
  dispatchErrorPopup: (message: string) => void,
  dispatchResetReport: () => void,
  dispatchResetAnalysis: () => void,
  dispatchResetQueriesSingle: () => void,
  dispatchResetQueriesMulti: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isActionEvent: boolean,
  isValidForm: boolean
};

const SHARED_MSG_KEY = 'sov_shared';

class SOVReportPageCollabaryInstagram extends React.Component<Props, State> {
  state = {
    isActionEvent: false,
    isValidForm: true
  };

  componentDidMount() {
    const { brandProfileId, reportId, dispatchFetchReport, dispatchFetchCountries } = this.props;

    if (brandProfileId && reportId) {
      dispatchFetchCountries();
      dispatchFetchReport(brandProfileId, reportId)
        .then(this.handleFetchResidualData);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetReport,
      dispatchResetAnalysis,
      dispatchResetQueriesSingle,
      dispatchResetQueriesMulti
    } = this.props;

    dispatchResetReport();
    dispatchResetAnalysis();
    dispatchResetQueriesSingle();
    dispatchResetQueriesMulti();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      report,
      isReportSaved,
      hasAnalysisBeenRun,
      isAnalysisDownloading,
      graphInstance,
      queriesSingle,
      queriesMulti,
      queriesMerged,
      isLoadingQueries
    } = this.props;

    const { isActionEvent, isValidForm } = this.state;

    return !isEqual(report, nextProps.report)
      || !isEqual(queriesSingle, nextProps.queriesSingle)
      || !isEqual(queriesMulti, nextProps.queriesMulti)
      || !isEqual(queriesMerged, nextProps.queriesMerged)
      || !isEqual(graphInstance, nextProps.graphInstance)
      || isReportSaved !== nextProps.isReportSaved
      || hasAnalysisBeenRun !== nextProps.hasAnalysisBeenRun
      || isAnalysisDownloading !== nextProps.isAnalysisDownloading
      || isLoadingQueries.single !== nextProps.isLoadingQueries.single
      || isLoadingQueries.multi !== nextProps.isLoadingQueries.multi
      || isActionEvent !== nextState.isActionEvent
      || isValidForm !== nextState.isValidForm;
  }

  setFormStatus() {
    this.setState({
      isValidForm: this.getIsAnalysisFiltersValid()
    });
  }

  getIsAnalysisFiltersValid(): boolean {
    const { report } = this.props;

    return Boolean(isReadyForAnalysis(report) && this.getIsOneQueryEnabledStatus());
  }

  getIsOneQueryEnabledStatus(): boolean {
    const { queriesMerged } = this.props;

    return queriesMerged.some(item => item.enabled);
  }

  getPreparedQueryResultsSingle(data: SOVQuerySave): Partial<SOVQueryCommon> {
    const { results, resultsCustom, channels } = this.props;

    if (data.isMulti) {
      return {};
    }

    return prepareQueryResultsSingleForEndpoint(
      { included: results, custom: resultsCustom, channels },
      data.query.id
    );
  }

  getPreparedQueryResultsMulti(data: SOVQuerySave): Partial<SOVQueryEndpointMultiGroup> {
    const { results, resultsCustom, channels } = this.props;

    if (!data.isMulti) {
      return {};
    }

    return prepareQueryResultsMultiForEndpoint(
      { included: results, custom: resultsCustom, channels },
      data.query.ids
    );
  }

  getEndpointData(data: SOVQuerySave): SOVQueryEndpoint {
    const { reportId } = this.props;
    const { query, isMulti, textFields } = data;
    const single = this.getPreparedQueryResultsSingle(data);
    const multi = this.getPreparedQueryResultsMulti(data);

    return getEndpointData(single, multi, reportId, textFields, query.ids, isMulti);
  }

  handleFetchResidualData = (): void => {
    const { reportId, dispatchFetchQueriesSingle, dispatchFetchQueriesMulti } = this.props;

    if (reportId) {
      dispatchFetchQueriesSingle(reportId);
      dispatchFetchQueriesMulti(reportId);
    }
  };

  handleTrackReportEvent(eventType: SOVGeneralEventType, reportId?: number | null): number {
    const { brandProfileId, report } = this.props;
    const updatedReportId = reportId || -1;
    const reportType = report.reportType;

    trackSOVEvent(eventType, REPORT, reportType, brandProfileId, updatedReportId);

    return updatedReportId;
  }

  handleTrackQueryEvent(
    eventType: SOVGeneralEventType,
    queryId: number | undefined | null,
    queryType: SOVQueryType
  ): number {
    const { brandProfileId } = this.props;
    const updatedQueryId = queryId || -1;

    trackSOVEvent(eventType, QUERY, queryType, brandProfileId, updatedQueryId);

    return updatedQueryId;
  }

  handleTrackAnalysisEvent(eventType: SOVGeneralEventType): void {
    const { report, brandProfileId } = this.props;

    trackSOVEvent(eventType, ANALYSIS, ANALYSIS, brandProfileId, report.id);
  }

  handleActionEventToggle = (status: boolean): void => {
    this.setState({
      isActionEvent: status
    });
  };

  handleTimeframeChange = (startDate?: string | null, endDate?: string | null): void => {
    this.handleUpdateReport({
      startDate,
      endDate
    });
  };

  handleCountriesChange = (countries: Array<string | undefined>): void => {
    this.handleUpdateReport({ countries });
  };

  handleDownloadAnalysis = (): void => {
    const { report, graphInstance, dispatchDownloadAnalysis } = this.props;
    const graphImageUrl = graphInstance
      ? graphInstance.getDataURL({ backgroundColor: '#fff' })
      : '';

    this.handleTrackAnalysisEvent('downloaded');
    dispatchDownloadAnalysis(report, graphImageUrl, 'spreadsheet');
  };

  handleUpdateReport = (props: SOVGeneralProps): void => {
    const { report, onReportSavedText, dispatchUpdateReport } = this.props;

    // $FlowIgnore - Flow cannot determine a type for object literal
    const updatedReport = {
      ...report,
      ...props
    };

    dispatchUpdateReport(updatedReport)
      .then(() => this.handleTrackReportEvent('updated', updatedReport.id))
      .then(onReportSavedText)
      .catch(() => this.handleDispatchErrorPopup('updating', REPORT));
  };

  handleDuplicateQuerySingle(query: SOVQueryEndpoint): void {
    const { reportId, dispatchDuplicateQuerySingle } = this.props;
    const queryId = query.id;

    if (queryId) {
      this.handleActionEventToggle(true);

      dispatchDuplicateQuerySingle(reportId, queryId)
        .then(results => {
          this.handleActionEventToggle(false);
          return this.handleTrackQueryEvent('duplicated', results?.payload?.query.id, SINGLE);
        })
        .catch(() => this.handleDispatchErrorPopup('duplicating', QUERY));
    }
  }

  handleDuplicateQueryMulti(query: SOVQueryEndpoint): void {
    const { reportId, dispatchDuplicateQueryMulti } = this.props;
    const queryId = query.id;

    if (queryId) {
      this.handleActionEventToggle(true);

      dispatchDuplicateQueryMulti(reportId, queryId)
        .then(results => {
          this.handleActionEventToggle(false);
          return this.handleTrackQueryEvent('duplicated', results?.payload?.query.id, MULTI);
        })
        .catch(() => this.handleDispatchErrorPopup('duplicating', QUERY));
    }
  }

  handleDuplicateQuery = (
    {
      query
    }: SOVGeneralProps
  ): void => {
    if (query.isMulti) {
      this.handleDuplicateQueryMulti(query);
      return;
    }

    this.handleDuplicateQuerySingle(query);
  };

  handleDeleteQuerySingle(reportId?: number | null, queryId?: number | null): void {
    const { dispatchDeleteQuerySingle } = this.props;

    if (reportId && queryId) {
      this.handleActionEventToggle(true);

      dispatchDeleteQuerySingle(reportId, queryId)
        .then(() => {
          this.handleActionEventToggle(false);
          return this.handleTrackQueryEvent('deleted', queryId, SINGLE);
        })
        .catch(() => this.handleDispatchErrorPopup('deleting', QUERY));
    }
  }

  handleDeleteQueryMulti(reportId?: number | null, queryId?: number | null): void {
    const { dispatchDeleteQueryMulti } = this.props;

    if (reportId && queryId) {
      this.handleActionEventToggle(true);

      dispatchDeleteQueryMulti(reportId, queryId)
        .then(() => {
          this.handleActionEventToggle(false);
          return this.handleTrackQueryEvent('deleted', queryId, MULTI);
        })
        .catch(() => this.handleDispatchErrorPopup('deleting', QUERY));
    }
  }

  handleDeleteQuery = (
    {
      reportId,
      queryId,
      isMulti
    }: SOVGeneralProps
  ): void => {
    if (isMulti) {
      this.handleDeleteQueryMulti(reportId, queryId);
      return;
    }

    this.handleDeleteQuerySingle(reportId, queryId);
  };

  handleUpdateQuerySingle(endpointData: SOVQueryEndpoint): void {
    const { dispatchUpdateQuerySingle } = this.props;

    this.handleActionEventToggle(true);

    dispatchUpdateQuerySingle(endpointData)
      .then(results => {
        this.handleActionEventToggle(false);
        return this.handleTrackQueryEvent('updated', results?.payload?.query.id, SINGLE);
      })
      .catch(() => this.handleDispatchErrorPopup('updating', QUERY));
  }

  handleUpdateQueryMulti(endpointData: SOVQueryEndpoint): void {
    const { dispatchUpdateQueryMulti } = this.props;

    this.handleActionEventToggle(true);

    dispatchUpdateQueryMulti(endpointData)
      .then(results => {
        this.handleActionEventToggle(false);
        return this.handleTrackQueryEvent('updated', results?.payload?.query.id, MULTI);
      })
      .catch(() => this.handleDispatchErrorPopup('updating', QUERY));
  }

  handleUpdateQuery = (endpointData: SOVQueryEndpoint, isMulti: boolean): void => {
    if (isMulti) {
      this.handleUpdateQueryMulti(endpointData);
      return;
    }

    this.handleUpdateQuerySingle(endpointData);
  };

  handleCreateQuerySingle(endpointData: SOVQueryEndpoint): void {
    const { dispatchCreateQuerySingle } = this.props;

    this.handleActionEventToggle(true);

    dispatchCreateQuerySingle(endpointData)
      .then(results => {
        this.handleActionEventToggle(false);
        return this.handleTrackQueryEvent('created', results?.payload?.query.id, SINGLE);
      })
      .catch(() => this.handleDispatchErrorPopup('creating', QUERY));
  }

  handleCreateQueryMulti(endpointData: SOVQueryEndpoint): void {
    const { dispatchCreateQueryMulti } = this.props;

    this.handleActionEventToggle(true);

    dispatchCreateQueryMulti(endpointData)
      .then(results => {
        this.handleActionEventToggle(false);
        return this.handleTrackQueryEvent('created', results?.payload?.query.id, MULTI);
      })
      .catch(() => this.handleDispatchErrorPopup('creating', QUERY));
  }

  handleCreateQuery(endpointData: SOVQueryEndpoint, isMulti: boolean): void {
    if (isMulti) {
      this.handleCreateQueryMulti(endpointData);
      return;
    }

    this.handleCreateQuerySingle(endpointData);
  }

  handleSaveQuery = (data: SOVQuerySave): void => {
    const { query, isMulti } = data;
    const endpointData = this.getEndpointData(data);
    const isEditMode = !isEmpty(query);

    const updatedEndpointData = {
      ...endpointData,
      enabled: isEditMode ? Boolean(query.enabled) : true
    };

    // edit mode
    if (isEditMode) {
      this.handleUpdateQuery(updatedEndpointData, isMulti);
      return;
    }

    // create mode
    this.handleCreateQuery(updatedEndpointData, isMulti);
  };

  handleDispatchErrorPopup(actionType: string, itemType: string): void {
    const { dispatchErrorPopup } = this.props;

    this.handleActionEventToggle(false);

    dispatchErrorPopup(
      __t(`${SHARED_MSG_KEY}.messages.failure`, {
        actionType: __t(`${SHARED_MSG_KEY}.labels.${actionType}`),
        itemType: __t(`${SHARED_MSG_KEY}.labels.${itemType}`).toLowerCase()
      })
    );
  }

  renderCalloutTypes(): React.ReactNode {
    const { organization, report, reportType } = this.props;

    if (organization?.isFreemium) {
      return (
        <SOVCalloutFreemium
          report={report}
          reportType={reportType}
          onSaveQuery={this.handleSaveQuery}
        />
      );
    }

    return (
      <SOVCalloutCollabary
        report={report}
        reportType={reportType}
        onSaveQuery={this.handleSaveQuery}
      />
    );
  }

  renderCallout(): React.ReactNode | undefined | null {
    const { queriesSingle, queriesMulti, isLoadingQueries } = this.props;

    if (isLoadingQueries.single || isLoadingQueries.multi) {
      return null;
    }

    if (isEmpty(queriesSingle) && isEmpty(queriesMulti)) {
      return this.renderCalloutTypes();
    }

    return null;
  }

  renderFilters(): React.ReactNode {
    const { report, countries, sovTimeframeCanSelectPastTwoWeeks } = this.props;
    const { isValidForm } = this.state;

    return (
      <SOVFilters
        isInvalidForm={!isValidForm}
        report={report}
        startDate={report.startDate}
        endDate={report.endDate}
        limitSelectableDates={sovTimeframeCanSelectPastTwoWeeks}
        countries={countries}
        onTimeframeChange={this.handleTimeframeChange}
        onCountriesChange={this.handleCountriesChange}
      />
    );
  }

  renderQueries(): React.ReactNode {
    const { report } = this.props;
    const { isActionEvent, isValidForm } = this.state;

    return (
      <SOVQueries
        pageType={COLLABARY}
        report={report}
        isInvalidQueriesSelection={!isValidForm && !this.getIsOneQueryEnabledStatus()}
        isActionEvent={isActionEvent}
        onUpdateReport={this.handleUpdateReport}
        onSaveQuery={this.handleSaveQuery}
        onUpdateQuery={this.handleUpdateQuery}
        onDuplicateQuery={this.handleDuplicateQuery}
        onDeleteQuery={this.handleDeleteQuery}
      />
    );
  }

  renderAnalysis(): React.ReactNode {
    const { organization, location, reportType, onChartInstanceCallback } = this.props;

    return (
      <SOVAnalysisResults
        reportType={reportType}
        chartInstanceCallback={onChartInstanceCallback}
        onFormStatusCheck={() => this.setFormStatus()}
        isValidSetup={this.getIsAnalysisFiltersValid()}
        location={location}
        isFreemium={Boolean(organization?.isFreemium)}
      />
    );
  }

  renderReport(): React.ReactNode | undefined | null {
    const { queriesSingle, queriesMulti } = this.props;
    const showReport = !isEmpty(queriesSingle) || !isEmpty(queriesMulti);

    return showReport && (
      <React.Fragment>
        {this.renderQueries()}
        {this.renderFilters()}
        {this.renderAnalysis()}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { onRenderBreadcrumbs, onRenderPageHeader, onRenderReportHeader } = this.props;

    return (
      <div className="sov-report-page-collabary sov-report-page-collabary-instagram">
        {onRenderBreadcrumbs()}
        {onRenderPageHeader()}
        {onRenderReportHeader(this.handleUpdateReport, this.handleDownloadAnalysis)}
        {this.renderReport()}
        {this.renderCallout()}
      </div>
    );
  }
}

const mapStateToProps = (
  {
    sovQueries,
    sovQueriesSingle,
    sovQueriesMulti,
    sovQueriesSearchResults,
    sovQueriesChannels,
    sovCountries,
    sovAnalysis
  }
): StateProps => ({
  queriesSingle: sovQueriesSingle.queries,
  queriesMulti: sovQueriesMulti.queries,
  queriesMerged: sovQueries.queriesMerged,
  results: sovQueriesSearchResults.results,
  resultsCustom: sovQueriesSearchResults.resultsCustom,
  channels: sovQueriesChannels.channels,
  countries: sovCountries.countries,
  hasAnalysisBeenRun: sovAnalysis.analysisHasBeenRun,

  isLoadingQueries: {
    single: sovQueriesSingle.isLoading,
    multi: sovQueriesMulti.isLoading
  }
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReport: (brandProfileId: number, reportId: number) => (
    dispatch(fetchReport(brandProfileId, reportId))
  ),

  dispatchUpdateReport: (report: SOVReport) => (
    dispatch(updateReport(report))
  ),

  dispatchFetchCountries: () => (
    dispatch(fetchCountries())
  ),

  dispatchFetchQueriesSingle: (reportId: number) => (
    dispatch(fetchQueriesSingle(reportId))
  ),

  dispatchFetchQueriesMulti: (reportId: number) => (
    dispatch(fetchQueriesMulti(reportId))
  ),

  dispatchCreateQuerySingle: (query: SOVQueryEndpoint) => (
    dispatch(createQuerySingle(query))
  ),

  dispatchCreateQueryMulti: (query: SOVQueryEndpoint) => (
    dispatch(createQueryMulti(query))
  ),

  dispatchUpdateQuerySingle: (query: SOVQueryEndpoint) => (
    dispatch(updateQuerySingle(query))
  ),

  dispatchUpdateQueryMulti: (query: SOVQueryEndpoint) => (
    dispatch(updateQueryMulti(query))
  ),

  dispatchDuplicateQuerySingle: (reportId: number, queryId: number) => (
    dispatch(duplicateQuerySingle(reportId, queryId))
  ),

  dispatchDuplicateQueryMulti: (reportId: number, queryId: number) => (
    dispatch(duplicateQueryMulti(reportId, queryId))
  ),

  dispatchDeleteQuerySingle: (reportId: number, queryId: number) => (
    dispatch(deleteQuerySingle(reportId, queryId))
  ),

  dispatchDeleteQueryMulti: (reportId: number, queryId: number) => (
    dispatch(deleteQueryMulti(reportId, queryId))
  ),

  dispatchDownloadAnalysis: (report: SOVReport, chartImage: string, format: string) => (
    dispatch(downloadAnalysis(report, chartImage, format))
  ),

  dispatchErrorPopup: (message: string) => (
    dispatch(danger(message))
  ),

  dispatchResetReport: () => (
    dispatch(resetReport())
  ),

  dispatchResetAnalysis: () => (
    dispatch(resetAnalysis())
  ),

  dispatchResetQueriesSingle: () => (
    dispatch(resetQueriesSingle())
  ),

  dispatchResetQueriesMulti: () => (
    dispatch(resetQueriesMulti())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVReportPageCollabaryInstagram);
